import { Box, Stack, Typography } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useFormik } from 'formik'
import { useCallback, useEffect, useMemo, type SyntheticEvent } from 'react'
import { useParams } from 'react-router-dom'

import { BackContinue } from '@components/back_continue/back_continue'
import { useBookingContext } from '@providers/booking'
import { MAX_WIDGET_SIZE, STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { shouldShowArrivalDeparturesBookingStep } from '@shared/booking_arrival_departures_flow'
import { continueNavigation } from '@shared/header_navigation'
import { i18WithParams as t } from '@shared/locale'
import {
  buildArrivalStepReservationView,
  mergeBookingConfigPreferSearchHours,
} from '@shared/surcharge_policy_arrival_adapters'
import { FlexCardDisplay, Spacer } from 'components-ui'
import { SurchargePolicy } from 'components-ui/src/purchase_summary/surcharge_policy/surcharge_policy'
import { isCheckInOutTimeEnabled } from 'components-ui/src/shared'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { useClientBookingConfigs } from 'service-api/src/hooks/use_client_booking_configs'
import { useClientPaymentSettings } from 'service-api/src/hooks/use_client_payment_settings'
import { useRoutes } from 'app/use_routes'

import { CheckInOut } from '@views/details_policies/check_in_out'
import { PanelSkeleton } from '@views/details_policies/panel_skeleton'
import { getArrivalDepartureCards } from './helper'
import {
  arrivalTimesInitialValues,
  arrivalTimesSchema,
  hasRequiredArrivalTimeValues,
} from './booking_arrival_departures_schema'

export const BookingArrivalDepartures = () => {
  const { accountName: locationName = '', serviceTypeName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const { navigateRoute } = useRoutes()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const serviceTypeNameForConfig =
    bookingCtx.state.data.serviceType?.serviceType?.name || bookingCtx.state.serviceTypeName || serviceTypeName || ''

  const bookingConfig = useClientBookingConfigs(locationName, serviceTypeNameForConfig, {
    enabled: Boolean(locationName && serviceTypeNameForConfig),
  })

  const locationBookingConfId = bookingConfig.data?.locationBookingConf?.id

  const paymentSettingsForSurcharge = useClientPaymentSettings(locationName, {
    locationBookingSurcharge: locationBookingConfId!,
    enabled: Boolean(locationName && locationBookingConfId),
  })

  const arrivalReservation = useMemo(
    () =>
      buildArrivalStepReservationView(
        bookingCtx.state,
        bookingConfig.data,
        bookingCtx.state.config.booking,
        paymentSettingsForSurcharge.data?.results
      ),
    [bookingCtx.state, bookingConfig.data, paymentSettingsForSurcharge.data?.results]
  )

  const surchargeBookingConfig = useMemo(
    () => mergeBookingConfigPreferSearchHours(bookingConfig.data, bookingCtx.state.config.booking),
    [bookingConfig.data, bookingCtx.state.config.booking]
  )

  useRedirectNoData()

  useEffect(() => {
    bookingCtx.dispatch({
      type: 'SET_SEARCH_STATE',
      payload: { value: 'arrivalDepartures', formHandler: 'arrival-departures-form' },
    })
  }, [bookingCtx])

  useEffect(() => {
    document.title = t('title.arrivalDepartures')
  }, [])

  useEffect(() => {
    if (
      bookingConfig.isSuccess &&
      bookingConfig.data?.locationBookingConf &&
      !isCheckInOutTimeEnabled(bookingConfig.data.locationBookingConf)
    ) {
      navigateRoute('detailsPolicies', { serviceTypeName: serviceTypeNameForConfig })
    }
  }, [bookingConfig.isSuccess, bookingConfig.data, navigateRoute, serviceTypeNameForConfig])

  const formik = useFormik({
    initialValues: arrivalTimesInitialValues(bookingCtx.state.cart),
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: arrivalReservation ? arrivalTimesSchema(arrivalReservation) : undefined,
    onSubmit: () => undefined,
  })

  const onContinueHandler = useCallback(
    async (ev?: SyntheticEvent) => {
      ev?.preventDefault()
      const errors = await formik.validateForm()
      if (Object.keys(errors).length > 0) {
        return false
      }
      bookingCtx.dispatch({
        type: 'SET_CHECK_IN_OUT_FORM',
        payload: { value: { fieldName: 'checkInTime', value: formik.values.checkInTime } },
      })
      bookingCtx.dispatch({
        type: 'SET_CHECK_IN_OUT_FORM',
        payload: { value: { fieldName: 'checkOutTime', value: formik.values.checkOutTime } },
      })
      const route = await continueNavigation(bookingCtx, locationName)
      if (route) {
        navigateRoute(route, { serviceTypeName: serviceTypeNameForConfig })
      }
      return false
    },
    [bookingCtx, formik, locationName, navigateRoute, serviceTypeNameForConfig]
  )

  const onBackHandler = useCallback(
    async (ev?: SyntheticEvent) => {
      ev?.preventDefault()
      navigateRoute('searchOffersResults', { serviceTypeName: serviceTypeNameForConfig })
    },
    [navigateRoute, serviceTypeNameForConfig]
  )

  const pageLoading =
    bookingConfig.isLoading || (Boolean(locationBookingConfId) && paymentSettingsForSurcharge.isLoading)
  const showForm =
    bookingConfig.isSuccess &&
    !!arrivalReservation &&
    shouldShowArrivalDeparturesBookingStep(bookingCtx.state) &&
    isCheckInOutTimeEnabled(arrivalReservation.locationBookingConf!)

  const arrivalDepartureCards = useMemo(() => {
    return getArrivalDepartureCards(bookingConfig.data?.flexBookingConfs)
  }, [bookingConfig.data?.flexBookingConfs])

  const syntheticOrder = arrivalReservation?.order

  const continueEnabled =
    hasRequiredArrivalTimeValues(arrivalReservation, formik.values) && formik.isValid && !formik.isValidating

  return (
    <Box>
      <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
        <Spacer height={3} />
        <Typography variant="h1" sx={{ px: isMobile ? 2 : 0 }}>
          {t('arrivalDepartures.title')}
        </Typography>
        <Spacer height={2} />
        {pageLoading ? (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flex={1} width="100%">
            <Stack direction="column" spacing={2} flex={1} width="100%">
              <PanelSkeleton />
              <PanelSkeleton />
            </Stack>
            <Stack direction="column" spacing={2} flex={1} width="100%">
              <PanelSkeleton />
            </Stack>
          </Stack>
        ) : showForm ? (
          <form id="arrival-departures-form" action="" onSubmit={onContinueHandler} style={{ width: '100%' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems="start" spacing={2} flex={1} width="100%" mb={2}>
              <Stack direction="column" spacing={3} flex={1} width="100%">
                <CheckInOut formik={formik} reservation={arrivalReservation} fieldRefs={undefined} />
                {arrivalDepartureCards.map((cardData, index) => (
                  <FlexCardDisplay key={index} cardData={cardData} variant="body1" />
                ))}
              </Stack>
              <Stack direction="column" spacing={3} flex={1} width="100%" mt={{ xs: 2, md: 0 }}>
                {syntheticOrder && surchargeBookingConfig && (
                  <SurchargePolicy order={syntheticOrder} bookingConfig={surchargeBookingConfig} />
                )}
              </Stack>
            </Stack>
            <Spacer height={STICKY_FOOTER_HEIGHT} />
            <BackContinue onBack={onBackHandler} onContinue={onContinueHandler} continueEnabled={continueEnabled} />
          </form>
        ) : null}
      </Box>
    </Box>
  )
}
